import type { FC } from 'react';
import type { ButtonProps } from '@components/Button';
import Button from '@components/Button';
import { Show, ShowFirstMatching } from '@components/Show';
import { isEmptyValue } from '@root/helpers';

import type { CreateRenewalFormState, StepProps } from '../types';
import { Step } from '../types';

interface FormFooterProps extends StepProps {
  isSubmitting: boolean;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
}

const getIsButtonDisabled = (formValues: CreateRenewalFormState, step: Step, isLoading: boolean) => {
  const isPreSelectionStep = step === Step.PreSelect;
  const isFirstStep = step === Step.Base;
  const isSecondStep = step === Step.Type;
  const isThirdStep = step === Step.Config;

  const hasSelectedSubmission = !isEmptyValue(formValues.renewFromSubmissionId);
  const hasName = !isEmptyValue(formValues.name);
  const hasSelectedTemplate = !isEmptyValue(formValues.baseTemplateId);
  const hasSelectedType = !isEmptyValue(formValues.type);

  if (isLoading) {
    return true;
  }

  if (isPreSelectionStep) {
    return !hasSelectedSubmission;
  }

  if (isFirstStep) {
    return !hasName;
  }

  if (isSecondStep) {
    return !hasSelectedType;
  }

  if (isThirdStep) {
    return !hasSelectedTemplate;
  }

  return false;
};

export const FormFooter: FC<FormFooterProps> = ({
  formValues,
  isLoading,
  isSubmitting,
  onClose,
  onNext,
  onPrevious,
  step,
}) => {
  const isSelectionStep = step === Step.PreSelect;
  const isFirstStep = step === Step.Base;
  const isSecondStep = step === Step.Type;
  const isReplicate = formValues.type === 'replicate';

  const isStandaloneMode = !isEmptyValue(formValues.renewFromSubmissionId);

  const handleBack = () => {
    if (isSelectionStep) {
      return onClose();
    }

    if (isFirstStep) {
      return isStandaloneMode ? onPrevious() : onClose();
    }

    return onPrevious();
  };

  const getBackButtonLabel = () => {
    if (isSelectionStep) return 'Cancel';
    if (isFirstStep) return isStandaloneMode ? 'Previous' : 'Cancel';
    return 'Back';
  };

  const buttonProps: ButtonProps = {
    className: 'h-9.5 w-full',
    isDisabled: getIsButtonDisabled(formValues, step, isLoading),
    kind: 'primary',
    loading: isLoading,
  };

  return (
    <div className="mt-4 flex w-full justify-between gap-4">
      <Button isDisabled={isSubmitting} onClick={handleBack} className="h-9.5 w-full">
        {getBackButtonLabel()}
      </Button>
      <ShowFirstMatching>
        <Show when={isSelectionStep || isFirstStep || (isSecondStep && !isReplicate)}>
          <Button {...buttonProps} onClick={onNext} type="button">
            Next
          </Button>
        </Show>
        <Show when={true}>
          <Button {...buttonProps} type="submit">
            Create
          </Button>
        </Show>
      </ShowFirstMatching>
    </div>
  );
};
